// Copyright (c) 2026 CTO's Club LLC. All rights reserved.
// Author: Alex Baretta (alex@baretta.com).

import {
    CLOVER_OAUTH_COMPLETION_CHANNEL,
    CloverOAuthCompletionReason,
    CloverOAuthCompletionResult,
    type CloverOAuthCompletion,
} from '@pillar-portal/api-types/ipg/clover/heartbeat';
import { z } from 'zod';

/** The message the completion route sends to the Portal tab that started the connection. */
const CloverOAuthCompletionMessageSchema = z.discriminatedUnion('result', [
    z.object({
        type: z.literal('clover-oauth-completion'),
        result: z.literal(CloverOAuthCompletionResult.CONNECTED),
    }),
    z.object({
        type: z.literal('clover-oauth-completion'),
        result: z.literal(CloverOAuthCompletionResult.ERROR),
        reason: z.enum(CloverOAuthCompletionReason),
    }),
]);

/** Reads the result the callback redirect carries; anything else is not a completion. */
export function parseCloverOAuthCompletion(
    searchParams: URLSearchParams
): CloverOAuthCompletion | null {
    const result = searchParams.get('result');
    if (result === CloverOAuthCompletionResult.CONNECTED) return { result };
    const reason = z.enum(CloverOAuthCompletionReason).safeParse(searchParams.get('reason'));
    if (result === CloverOAuthCompletionResult.ERROR && reason.success)
        return { result, reason: reason.data };
    return null;
}

/** Friendly words for a completion; a reason code is never shown. */
export function describeCloverOAuthCompletion(completion: CloverOAuthCompletion): string {
    if (completion.result === CloverOAuthCompletionResult.CONNECTED)
        return "Clover is connected. Anchorbase is setting up this organization's Clover terminals.";
    switch (completion.reason) {
        case CloverOAuthCompletionReason.INCOMPLETE:
            return "Clover didn't finish the authorization. Choose Connect Clover to try again.";
        case CloverOAuthCompletionReason.EXPIRED:
            return 'The connection request expired or was already used. Choose Connect Clover to start again.';
        case CloverOAuthCompletionReason.MERCHANT_MISMATCH:
            return 'Clover signed in to a different merchant than this organization uses. Sign in to its Clover merchant and choose Connect Clover again.';
        case CloverOAuthCompletionReason.CLIENT_MISMATCH:
            return 'Clover answered for a different Anchorbase app. Choose Connect Clover to try again; if it keeps happening, contact support.';
        case CloverOAuthCompletionReason.PROVIDER_REJECTED:
            return 'Clover did not accept the connection. Choose Connect Clover to sign in to Clover again.';
        case CloverOAuthCompletionReason.UNAVAILABLE:
            return 'Clover could not be connected right now. Try again in a few minutes; if it keeps happening, contact support.';
    }
}

/** The window that opened the completion route, when the provider kept it. */
export type CloverOAuthCompletionOpener = Pick<Window, 'postMessage'>;

/** The browser surfaces the completion route uses to reach the Portal tabs. */
export interface CloverOAuthCompletionNotifier {
    readonly openChannel: (name: string) => Pick<BroadcastChannel, 'postMessage' | 'close'>;
    readonly opener: CloverOAuthCompletionOpener | null;
    readonly origin: string;
}

/**
 * Tells the Portal tabs a connection finished: every same-origin tab through
 * the BroadcastChannel, and the opener directly when the provider's pages left
 * it in place.
 */
export function publishCloverOAuthCompletion(
    completion: CloverOAuthCompletion,
    notifier: CloverOAuthCompletionNotifier
): void {
    const message = { type: 'clover-oauth-completion', ...completion };
    const channel = notifier.openChannel(CLOVER_OAUTH_COMPLETION_CHANNEL);
    try {
        channel.postMessage(message);
    } finally {
        channel.close();
    }
    notifier.opener?.postMessage(message, notifier.origin);
}

/**
 * Calls back for each completion another Portal tab or the connection window
 * reports; returns the unsubscribe function.
 */
export function subscribeToCloverOAuthCompletion(
    onCompletion: (completion: CloverOAuthCompletion) => void,
    target: {
        readonly openChannel: (name: string) => BroadcastChannel;
        readonly window: Pick<Window, 'addEventListener' | 'removeEventListener'>;
        readonly origin: string;
    }
): () => void {
    const handle = (data: unknown): void => {
        const message = CloverOAuthCompletionMessageSchema.safeParse(data);
        if (!message.success) return;
        const { type: _type, ...completion } = message.data;
        onCompletion(completion);
    };
    const channel = target.openChannel(CLOVER_OAUTH_COMPLETION_CHANNEL);
    const onChannelMessage = (event: MessageEvent<unknown>): void => handle(event.data);
    const onWindowMessage = (event: MessageEvent<unknown>): void => {
        if (event.origin === target.origin) handle(event.data);
    };
    channel.addEventListener('message', onChannelMessage);
    target.window.addEventListener('message', onWindowMessage);
    return () => {
        channel.removeEventListener('message', onChannelMessage);
        channel.close();
        target.window.removeEventListener('message', onWindowMessage);
    };
}
